import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { PanZoom } from '../../../view/PanZoom';
import { ModifierKeys } from '../../ModifierKeys';
import { StaticBrushCursorAction } from '../../actions/contouring-actions/StaticBrushCursorAction';
import { StaticBrushDrawAction } from '../../actions/contouring-actions/StaticBrushDrawAction';
import { BrushTool } from './BrushTool';
import { ReadonlyVec2 } from 'gl-matrix';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';

/**
 * {@link StaticBrushTool} is a {@link BrushTool} that can be used to draw changes on contours.\
 * It can draw both planar circles as well as 3D spheres.
 */
export class StaticBrushTool extends BrushTool {
    /**
     * {@link ModifierKeys} that need to be pressed in order to change the size of the brush.
     */
    public static readonly BRUSH_SIZE_MODIFIER_KEYS = ModifierKeys.SHIFT;

    /**
     * {@link StaticBrushTool} constructor, which takes a {@link PanZoom} instance as an argument.\
     * It calls the {@link BrushTool super} {@link BrushTool.constructor constructor} with the {@link panZoom} as an argument.\
     * The {@link BrushTool super} {@link BrushTool.constructor constructor} initializes the different actions that are used by the {@link StaticBrushTool}.
     * @param panZoom {@link PanZoom} that is needed to provide panning and zooming functionality.
     */
    public constructor(
        private readonly image: ImageObject,
        protected readonly structure: RegionOfInterest,
        panZoom: PanZoom,
        erase: boolean,
        brushDiameter: number,
        brushSizeChangedCallback: (brushSize: number) => void
    ) {
        super(
            panZoom,
            erase,
            brushDiameter,
            structure,
            brushSizeChangedCallback
        );
    }

    /**
     * Method to create a {@link StaticBrushCursorAction} instance, which is the type of {@link CursorAction} used by {@link StaticBrushTool}.
     * @returns A new {@link StaticBrushCursorAction} instance, with diameter set to {@link StaticBrushTool.diameter}.
     */
    protected resetCursorAction(): void {
        if (this._cursorAction) {
            this._cursorAction.stopAction();
        }
        this._cursorAction = new StaticBrushCursorAction(
            this.image,
            this._diameter,
            this.structure.color
        );
    }

    protected resetDrawAction(): void {
        this._drawAction?.stopAction();
        this._drawAction = new StaticBrushDrawAction(
            this.structure,
            this.image,
            this._diameter,
            this._erase,
            0,
            1
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: PlaneView
    ): EventHandlerResult {
        view.snapToImagePixel();
        if (buttons === 0 && this._drawState) {
            this._drawState.clearMemory();
            this._drawState = undefined;
        }
        if (this._drawState) {
            return this._drawState.onMouseMove(coordinates, buttons, view);
        }
        if (
            view instanceof PlaneView &&
            view.label === Orientations.TRANSVERSAL_ORIENTATION &&
            this.structure.dicomParent.approvalStatus !==
                DICOMApprovalStatus.APPROVED
        ) {
            if (!this._cursorAction) {
                this.resetCursorAction();
            }
            return (
                this._cursorAction?.onMouseMove(coordinates, buttons, view) ??
                new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED)
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }
}
